import React, { useState, useEffect } from 'react';
import Seo from '../../components/seo';
import Body from '../../components/body';
import Layout from '../../components/layout';
import Heading from '../../components/heading';
import { graphql } from 'gatsby';
import Url from '../../components/url';
import { csv } from 'd3-fetch';
import { scaleBand, scaleLinear } from 'd3-scale';
import { max, ascending, group, sum, } from 'd3-array';
import Code from '../../components/code';
import Loading from '../../components/loading';

const HorizontalBar4 = ({ data }) => {
    const femaleColor = "#fc8896";
    const maleColor = "#3e6597";
    const location = data.allFile.nodes[0].publicURL
    const margin = ({ top: 50, bottom: 20, left: 40, right: 20 })
    const [datas, setDatas] = useState(null)
    const [width, setWidth] = useState(400)
    const height = 600;
    const updateDimensions = () => {
        setWidth(window.outerWidth);
    }
    useEffect(() => {
        csv(location).then(value => {
            const newValue = Array.from(group(value, d => d.Year))
                .sort((a, b) => ascending(a[0], b[0]))
                .map(a => ({
                    Year: a[0],
                    Female: Math.round((sum(a[1], d => d.Sex === 'F') / a[1].length) * 100),
                    Male: Math.round((sum(a[1], d => d.Sex === 'M') / a[1].length) * 100),
                    Total: 100
                }))
            setDatas(newValue)
        })
    }, [location])
    useEffect(() => {
        setWidth(window.outerWidth);
        window.addEventListener("resize", updateDimensions);
        return () => window.removeEventListener("resize", updateDimensions);
    }, []);
    const yScale = datas ?
        scaleBand()
            .domain(datas.map(d => d.Year))
            .range([margin.top, height - margin.bottom])
            .padding(0.2)
        : null
    const xScale = datas ?
        scaleLinear()
            .domain([0, max(datas, d => d.Total)])
            .range([0, width - 3 * margin.right - margin.left - 20])
        : null
    return (
        <Layout>
            <Seo title="Horizontal Bar 4" />
            <Heading heading="Horizontal Bar-IV" />
            <Body>
                <p>
                    The best form of practice is imitation.So today I will try to immitate a graph that
                    I saw on <Url
                        href="r/dataisbeautiful"
                        name="r/dataisbeautiful"
                        url="https://www.reddit.com/r/dataisbeautiful/comments/pajewy/120_years_of_olympics_sex_distrubition_oc/" />
                    .It is similar to the previous template. We do need to curate the data to match the data that
                    we feed to our bar chart component. The data set for this graph can be found at
                    <Url
                        url="https://www.mavenanalytics.io/data-playground"
                        href="olympics data"
                        name="olympics data set" />.
                    So let's get started.
                    <br />
                    <br />
                    The first objective of visualization is to curate the data. So let's look at the data set.
                </p>
                <Code>
                    {`
"ID","Name","Sex","Age","Height","Weight","Team","NOC","Games","Year","Season","City","Sport","Event","Medal"
"1","A Dijiang","M",24,180,80,"China","CHN","1992 Summer",1992,"Summer","Barcelona","Basketball","Basketball Men's Basketball",NA
"2","A Lamusi","M",23,170,60,"China","CHN","2012 Summer",2012,"Summer","London","Judo","Judo Men's Extra-Lightweight",NA
"3","Gunnar Nielsen Aaby","M",24,NA,NA,"Denmark","DEN","1920 Summer",1920,"Summer","Antwerpen","Football","Football Men's Football",NA
"4","Edgar Lindenau Aabye","M",34,NA,NA,"Denmark/Sweden","DEN","1900 Summer",1900,"Summer","Paris","Tug-Of-War","Tug-Of-War Men's Tug-Of-War","Gold"
"5","Christine Jacoba Aaftink","F",21,185,82,"Netherlands","NED","1988 Winter",1988,"Winter","Calgary","Speed Skating","Speed Skating Women's 500 metres",NA
"5","Christine Jacoba Aaftink","F",21,185,82,"Netherlands","NED","1988 Winter",1988,"Winter","Calgary","Speed Skating","Speed Skating Women's 1,000 metres",NA
"5","Christine Jacoba Aaftink","F",25,185,82,"Netherlands","NED","1992 Winter",1992,"Winter","Albertville","Speed Skating","Speed Skating Women's 500 metres",NA
"5","Christine Jacoba Aaftink","F",25,185,82,"Netherlands","NED","1992 Winter",1992,"Winter","Albertville","Speed Skating","Speed Skating Women's 1,000 metres",NA
"5","Christine Jacoba Aaftink","F",27,185,82,"Netherlands","NED","1994 Winter",1994,"Winter","Lillehammer","Speed Skating","Speed Skating Women's 500 metres",NA
"5","Christine Jacoba Aaftink","F",27,185,82,"Netherlands","NED","1994 Winter",1994,"Winter","Lillehammer","Speed Skating","Speed Skating Women's 1,000 metres",NA
"6","Per Knut Aaland","M",31,188,75,"United States","USA","1992 Winter",1992,"Winter","Albertville","Cross Country Skiing","Cross Country Skiing Men's 10 kilometres",NA`}
                </Code>
                <p>
                    It is in csv format and has lot of data points. The above list is just a few
                    lines from the data set file. As per the graph created in the post we will need year,male,femal participations.
                    It will be much more better if the data is preprocessed with some better tools like excel,pandas-python,R than feed
                    into d3 but we will try it purely with d3.
                </p>
                <Code>
                    {`
d3.csv(location)
    .then(
        process and return
    )`}
                </Code>
                <p>
                    So for data curation. I grouped the data by year, sorted in ascending order
                    by year than calucated the total number of male and female athletes. I used d3
                    group,sum and ascending function for the processing. I read the data from a file
                    then I grouped them with d3.group, sorted the result with d3.ascending. Lastly, created
                    an array with 4 columns with the help of d3.sum .
                </p>
                <Code>
                    {`
csv(location).then(value => {
    const newValue = Array.from(group(value, d => d.Year))
        .sort((a, b) => ascending(a[0], b[0]))
        .map(a => ({
            Year: a[0],
            Female: sum(a[1], d => d.Sex === 'F'),
            Male: sum(a[1], d => d.Sex === 'M'),
            Total: a[1].length
        }))
    setDatas(newValue)
})
`}</Code>
                <p>So the new data looks like</p>
                <Code>
                    {`{Year: "1896", Female: 0, Male: 380, Total: 380}
{Year: "1900", Female: 33, Male: 1903, Total: 1936}
{Year: "1904", Female: 16, Male: 1285, Total: 1301}
{Year: "1906", Female: 11, Male: 1722, Total: 1733}
{Year: "1908", Female: 47, Male: 3054, Total: 3101}
{Year: "1912", Female: 87, Male: 3953, Total: 4040}
{Year: "1920", Female: 134, Male: 4158, Total: 4292}`}
                </Code>
                <p>
                    Let's start carving the pie
                </p>
                {
                    datas
                        ?
                        <div className="my-2 sm:my-4 my-6 border dark:bg-gray-500 bg-gray-200 rounded">
                            <svg
                                width={width - 50}
                                height={height}
                                className="fill-current text-blue-400 dark:text-white">
                                <text className="text-gray-700 dark:text-white" key="xindex" x={(width) / 2} y={margin.top * 0.4}>Total Percentage →</text>
                                <g className="text-gray-700 dark:text-white" transform={`translate(${margin.left * 0.6},${margin.top + 5}) rotate(-90)`}>
                                    <text key="yIndex" >← Year</text>

                                </g>
                                {
                                    xScale.ticks().map((a, index) => (
                                        <g transform={`translate(${margin.left},0)`}
                                            className="stroke-current text-gray-400 dark:text-gray-50">
                                            <line
                                                key={`xTickLine-${index}`}
                                                x1={xScale(a)}
                                                y1={margin.top * 0.9}
                                                x2={xScale(a)}
                                                y2={margin.top} />
                                            <text
                                                className="text-xs font-thin text-gray-700 dark:text-white"
                                                key={`xTick-${index}`}
                                                x={xScale(a)}
                                                y={margin.top * 0.8}
                                                textAnchor="middle"
                                            >
                                                {`${a}%`}
                                            </text>
                                        </g>
                                    ))
                                }
                                {
                                    yScale.domain().map((a, index) => (
                                        <g key={`yScaleGroup-${index}`}>
                                            <text
                                                dy="0.35em"
                                                textAnchor="start"
                                                className="text-xs text-gray-700 dark:text-white"
                                                key={`yScaleText-${index}`}
                                                x={margin.left * 0.2}
                                                y={yScale(a) + yScale.bandwidth() / 2}>
                                                {a}
                                            </text>
                                        </g>
                                    ))
                                }
                                {
                                    datas.map((a, index) => (
                                        <rect
                                            key={`rectangle-${index}`}
                                            x={margin.left}
                                            y={yScale(a.Year)}
                                            width={xScale(a.Total)}
                                            height={yScale.bandwidth()} />
                                    ))
                                }

                            </svg>
                        </div>

                        : <Loading />
                }
                <p>
                    After the first projection above where I projected the total. I realized the reddit
                    plot was doing 100% of total athlete every olympic year and then divinding it into male and
                    female. I guess got carried away on copying stuff without making sense. So back to the data curation
                    step. Now I need to show male and female over hundered percentage.
                    First let see the male Percentage
                </p>
                {
                    datas
                        ?
                        <div className="my-2 sm:my-4 my-6 border dark:bg-gray-500 bg-gray-200 rounded">
                            <svg
                                width={width - 50}
                                height={height}
                                className="fill-current text-gray-700 dark:text-white">
                                <text className="text-gray-700 dark:text-white" key="xindex" x={(width) / 2} y={margin.top * 0.4}>Male Percentage →</text>
                                <g className="text-gray-700 dark:text-white" transform={`translate(${margin.left * 0.6},${margin.top + 5}) rotate(-90)`}>
                                    <text key="yIndex" >← Year</text>
                                </g>
                                {
                                    xScale.ticks().map((a, index) => (
                                        <g transform={`translate(${margin.left},0)`}
                                            className="stroke-current text-gray-400 dark:text-gray-50">
                                            <line
                                                key={`xTickLine-${index}`}
                                                x1={xScale(a)}
                                                y1={margin.top * 0.9}
                                                x2={xScale(a)}
                                                y2={margin.top} />
                                            <text

                                                className="text-xs font-thin text-gray-700 dark:text-white"
                                                key={`xTick-${index}`}
                                                x={xScale(a)}
                                                y={margin.top * 0.8}
                                                textAnchor="middle"
                                            >
                                                {`${a}%`}
                                            </text>
                                        </g>
                                    ))
                                }
                                {
                                    yScale.domain().map((a, index) => (
                                        <g key={`yScaleGroup-${index}`}>
                                            <text
                                                dy="0.35em"
                                                textAnchor="start"
                                                className="text-xs"
                                                key={`yScaleText-${index}`}
                                                x={margin.left * 0.2}
                                                y={yScale(a) + yScale.bandwidth() / 2}>
                                                {a}
                                            </text>
                                        </g>
                                    ))
                                }
                                {
                                    datas.map((a, index) => (
                                        <rect
                                            fill={maleColor}
                                            key={`rectangle-${index}`}
                                            x={margin.left}
                                            y={yScale(a.Year)}
                                            width={xScale(a.Male)}
                                            height={yScale.bandwidth()} />
                                    ))
                                }

                            </svg>
                        </div>

                        : <Loading />
                }
                <p>
                    Now looking at the female Athletes
                </p>
                {
                    datas
                        ?
                        <div className="my-2 sm:my-4 my-6 border dark:bg-gray-500 bg-gray-200 rounded">
                            <svg
                                width={width - 50}
                                height={height}
                                className="fill-current text-gray-700 dark:text-white">
                                <text className="text-gray-700 dark:text-white" key="xindex" x={(width) / 2} y={margin.top * 0.4}>Female Percentage →</text>
                                <g className="text-gray-700 dark:text-white" transform={`translate(${margin.left * 0.6},${margin.top + 5}) rotate(-90)`}>
                                    <text key="yIndex" >← Year</text>
                                </g>
                                {
                                    xScale.ticks().map((a, index) => (
                                        <g transform={`translate(${margin.left},0)`}
                                            className="stroke-current text-gray-400 dark:text-gray-50">
                                            <line
                                                key={`xTickLine-${index}`}
                                                x1={xScale(a)}
                                                y1={margin.top * 0.9}
                                                x2={xScale(a)}
                                                y2={margin.top} />
                                            <text
                                                className="text-xs font-thin text-gray-700 dark:text-white"
                                                key={`xTick-${index}`}
                                                x={xScale(a)}
                                                textAnchor="middle"
                                                y={margin.top * 0.8}
                                            >
                                                {`${a}%`}
                                            </text>
                                        </g>
                                    ))
                                }
                                {
                                    yScale.domain().map((a, index) => (
                                        <g key={`yScaleGroup-${index}`}>
                                            <text
                                                dy="0.35em"
                                                textAnchor="start"
                                                className="text-xs"
                                                key={`yScaleText-${index}`}
                                                x={margin.left * 0.2}
                                                y={yScale(a) + yScale.bandwidth() / 2}>
                                                {a}
                                            </text>
                                        </g>
                                    ))
                                }
                                {
                                    datas.map((a, index) => (

                                        <rect
                                            fill={femaleColor}
                                            key={`rectangle - ${index}`}
                                            x={margin.left}
                                            y={yScale(a.Year)}
                                            width={xScale(a.Female)}
                                            height={yScale.bandwidth()} />

                                    ))

                                }

                            </svg>
                        </div>

                        : <Loading />
                }

                <p>
                    Finally lets copy the example
                </p>
                {
                    datas
                        ?
                        <div className="my-2 sm:my-4 my-6 border dark:bg-gray-500 bg-gray-200 rounded">
                            <svg
                                width={width - 50}
                                height={height}
                                className="fill-current text-gray-700 dark:text-white">
                                <text className="text-gray-700 dark:text-white" key="xindex" x={(width) / 2} y={margin.top * 0.4}>Female Percentage →</text>
                                <g className="text-gray-700 dark:text-white" transform={`translate(${margin.left * 0.6},${margin.top + 5}) rotate(-90)`}>
                                    <text key="yIndex" >← Year</text>
                                </g>
                                {
                                    xScale.ticks().map((a, index) => (
                                        <g transform={`translate(${margin.left},0)`}
                                            className="stroke-current text-gray-400 dark:text-gray-50">
                                            <line
                                                key={`xTickLine-${index}`}
                                                x1={xScale(a)}
                                                y1={margin.top * 0.9}
                                                x2={xScale(a)}
                                                y2={margin.top} />
                                            <text
                                                className="text-xs font-thin text-gray-700 dark:text-white"
                                                key={`xTick-${index}`}
                                                x={xScale(a)}
                                                textAnchor="middle"
                                                y={margin.top * 0.8}
                                            >
                                                {`${a}%`}
                                            </text>
                                        </g>
                                    ))
                                }
                                {
                                    yScale.domain().map((a, index) => (
                                        <g key={`yScaleGroup-${index}`}>
                                            <text
                                                dy="0.35em"
                                                textAnchor="start"
                                                className="text-xs"
                                                key={`yScaleText-${index}`}
                                                x={margin.left * 0.2}
                                                y={yScale(a) + yScale.bandwidth() / 2}>
                                                {a}
                                            </text>
                                        </g>
                                    ))
                                }
                                {
                                    datas.map((a, index) => (
                                        <g>
                                            <rect
                                                fill={maleColor}

                                                x={margin.left}
                                                y={yScale(a.Year)}
                                                width={xScale(a.Male)}
                                                height={yScale.bandwidth()} />
                                            <g transform={`translate(${xScale(a.Male)},0)`}>
                                                <rect
                                                    fill={femaleColor}
                                                    key={`rectangleFemale - ${index}`}
                                                    x={margin.left}
                                                    y={yScale(a.Year)}
                                                    width={xScale(a.Female)}
                                                    height={yScale.bandwidth()} />
                                            </g>
                                        </g>
                                    ))
                                }
                            </svg>
                        </div>
                        : <Loading />
                }
                <p>
                    Basically the only trick on this graph was making two rectangle one over the other
                    and just translating one at the end of the other one. So I created two rectangle respectively
                </p>
                <Code>
                    {`
<rect
    fill={maleColor}
    x={margin.left}
    y={yScale(a.Year)}
    width={xScale(a.Male)}
    height={yScale.bandwidth()} />

    <rect
    fill={femaleColor}
    x={margin.left}
    y={yScale(a.Year)}
    width={xScale(a.Female)}
    height={yScale.bandwidth()} />`}
                </Code>
                <p>
                    Made a male rectangle at the back and female at the front. So now only thing
                    left is to move the female bar chart up to the end of the male chart by using translate
                    in the group svg component.
                </p>
                <Code>
                    {`
datas.map((a, index) => (
<g>
    <rect
        fill={maleColor}
        x={margin.left}
        y={yScale(a.Year)}
        width={xScale(a.Male)}
       height={yScale.bandwidth()} />
    <g transform={\`translate($\{xScale(a.Male),0)}\`}>
        <rect
            fill={femaleColor}        
            x={margin.left}
            y={yScale(a.Year)}
            width={xScale(a.Female)}
            height={yScale.bandwidth()} />
    </g>
</g>
))`}
                </Code>
                <p>
                    I had to add some back slash on transform as the code was not translating to
                    html on my end properly so remove all the back slash from it. Don't forget to
                    add keys to the components or else react will complain about it.
                </p>
            </Body>
        </Layout>
    )
}

export const query = graphql`
    query HorizontalBar4Query {
        allFile(filter: { base: { regex: "/.*olympics.*/" } }) {
            nodes {
                publicURL
            }
        }
}`

export default HorizontalBar4